import React, { Dispatch, SetStateAction } from "react";

import moment from "moment";

import { getCurrentProgramTermsAndConditionsByProgramId } from "services";

import { formatDate, queryIsLoading, saveFile, useGetCarbonProgramById, useGetProgramContractByProgramId, useTranslation } from "utils";

import { PROGRAM_TYPE, ProgramContractDetails, USER_TYPE, userType } from "models";

import { Checkbox, Flex, GuidanceLink, Spin, Tag } from "components";

type ProgramTermsProps = {
  contractDetails: ProgramContractDetails;
  userType: userType;
  setCheckboxState: Dispatch<SetStateAction<ProgramContractDetails[]>>;
};

export const ProgramTerms = ({ contractDetails, userType, setCheckboxState }: ProgramTermsProps) => {
  const { t } = useTranslation("dashboard");
  const programQuery = useGetCarbonProgramById({ programId: contractDetails.programId });
  const programContract = useGetProgramContractByProgramId({ programId: contractDetails.programId });

  const programType = programQuery.data?.partnerId ? PROGRAM_TYPE.INSETTING : PROGRAM_TYPE.OFFSETTING;

  const handleProgramTermsClick = async () => {
    const data = await getCurrentProgramTermsAndConditionsByProgramId(contractDetails.programId);
    saveFile(data, `${t("termsOverlay.programTermsFileName")}_${programQuery.data?.programName}.pdf`);
  };

  const handleCheckboxClick = () => {
    setCheckboxState((prev) =>
      prev.reduce(
        (acc, curr) => (curr.programId === contractDetails.programId ? [...acc, { ...curr, accepted: !curr.accepted }] : [...acc, curr]),
        [] as ProgramContractDetails[]
      )
    );
  };

  if ([programQuery].some(queryIsLoading)) {
    return <Spin size="small" />;
  }

  return (
    <div className="margin-top-250">
      <Flex align="center" gap="0.25rem" wrap="wrap">
        {userType === USER_TYPE.EXISTING && <Checkbox checked={contractDetails.accepted} onClick={handleCheckboxClick} />}
        <h3 className="margin-right-50">{programQuery.data?.programName}</h3>
        {contractDetails.farmNames.map((name, index) => (
          <Tag key={index} text={name} colour="yellow" />
        ))}
      </Flex>
      {userType === USER_TYPE.EXISTING && programContract.data?.createdAt && (
        <p>
          {t("termsOverlay.deadline", {
            createdAt: formatDate(programContract.data?.createdAt, "LL", moment.ISO_8601),
            signBy: formatDate(
              moment(programContract.data?.createdAt)
                .add(30, "days")
                .toISOString(),
              "LL",
              moment.ISO_8601
            ),
          })}
        </p>
      )}
      <p>{t(`termsOverlay.${userType}.programTermsDescription.${programType}`)}</p>
      <Flex align="center" gap="0.25rem">
        <span>{t("termsOverlay.linkDescription")}</span>
        <GuidanceLink text={t("termsOverlay.programTermsLink")} onClick={handleProgramTermsClick} />
      </Flex>
    </div>
  );
};

export default ProgramTerms;
